import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from 'src/hooks/useApiClient';
import { fromApiListItem, fromApiRecord, toApiBody } from './mapping';
import type {
	ConfigurationDraft,
	SavedCarConfiguration,
	SavedConfigurationsPage,
	SelectedOffer,
} from './types';

export const SAVED_CONFIGS_QUERY_KEY = 'mn_saved_configurations';
export const SAVED_CONFIG_QUERY_KEY = 'mn_saved_configuration';

export const useSavedConfigurations = (
	search: string,
	page: number,
	pageSize: number
) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [SAVED_CONFIGS_QUERY_KEY, search, page, pageSize],
		queryFn: async (): Promise<SavedConfigurationsPage> => {
			const res =
				await apiClient.motornetControllerService.getCarConfigurations({
					q: search || undefined,
					page,
					pageSize,
					sortBy: 'createdAt',
					sortDirection: 'desc',
				});
			return { items: res.items.map(fromApiListItem), total: res.total };
		},
		staleTime: 0,
		gcTime: 0,
		retry: false,
		refetchOnWindowFocus: false,
	});
};

export const useConfigurationFetcher = () => {
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();

	return (id: string): Promise<SavedCarConfiguration> =>
		queryClient
			.fetchQuery({
				queryKey: [SAVED_CONFIG_QUERY_KEY, id],
				queryFn: () =>
					apiClient.motornetControllerService.getCarConfiguration(id),
				staleTime: 0,
				gcTime: 0,
				retry: false,
			})
			.then(fromApiRecord);
};

export const useSaveConfiguration = () => {
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: {
			draft: ConfigurationDraft;
			code: string;
			offer: SelectedOffer;
		}) =>
			apiClient.motornetControllerService
				.saveCarConfiguration(toApiBody(input.draft, input.code, input.offer))
				.then(fromApiRecord),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: [SAVED_CONFIGS_QUERY_KEY] }),
	});
};

export const useDeleteConfiguration = () => {
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) =>
			apiClient.motornetControllerService.deleteCarConfiguration(id),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: [SAVED_CONFIGS_QUERY_KEY] }),
	});
};
